<template>
	<model-viewer
		:src="$store.state.activeModel"
		environment-image="neutral"
		camera-orbit="-131.5deg 75.77deg auto"
		min-camera-orbit="auto auto 80%"
		max-camera-orbit="auto 95deg 65%"
		animation-crossfade-duration="500"
		animation-control-mode="manual"
		interaction-prompt-style="basic"
		:interaction-prompt="modelPrompt"
		camera-controls
		:class="{ 'hotspots-visible': activePrimaryNavigation === 'keyFeatures' && activeSecondaryNavigation }"
		@progress="updateProgress"
		@load="onModelLoad"
		@camera-change="onCameraChange"
		v-html="$store.state.productConfig.hotspots"
	/>

  <!-- camera-orbit="-50deg 75deg 65%" -->

	<div
		v-if="$store.state.debug"
		class="debug-info"
	>
		Orbit: {{ cameraOrbit }}<br>
		Target: {{ cameraTarget }}
	</div>
</template>

<script>
import { mapState } from 'vuex'
import 'model-viewer'
// import '/Users/Roman/Work/Brainz/repos/model-viewer/dist/model-viewer.js'

export default {
	name: 'ModelView',
	props: {
		playing: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			modelPrompt: 'auto',
			modelPlaying: false,
			modelTime: 0,
			modelViewer: null,
			modelActions: {},
			hotspots: [],
			animationsInProgress: 0,

			cameraOrbit: '',
			cameraTarget: ''
		}
	},
	computed: mapState({
		activePrimaryNavigation: state => state.navigation.activePrimary,
		activeSecondaryNavigation: state => state.navigation.activeSecondary,
		activeSubExplanation: state => state.explanationPaneSub,
		explanationPaneOpen: state => state.explanationPaneOpen,
		animationsList: state => state.productConfig.animations,
		modelLoadingProgress: state => state.modelLoadingProgress,
		productConfig: state => state.productConfig,
    transferModeOn: state => state.navigation.transferModeOn,
    siderailsCheck: state => state.navigation.siderailsCheck
	}),
	mounted() { 
		this.$bus.on('requestModelAnimation', ({ name, direction, binary }) => {
			const binaryAnimationActive = binary && this.modelActions[name]?.time > 0      

      // bed height
      if (name === 'bed_height_adjustment_up' || name === 'bed_height_adjustment_down') {
        if (this.animationsInProgress > 0) return
        this.handleBedHeightAdjustment(name)
        return
      }
      // trendelenburg
      if ((name === 'trendelenburg' || name === 'reverse_trendelenburg')) {
        if (this.animationsInProgress > 0) return
        this.handleTrendelenburg(name)
        return
      }
      // backrest
      if (name === 'backrest_adjustment') {
        if (this.animationsInProgress > 0) return
        this.handleBackrestAdjustment(direction)
        return
      }
      // legrest
      if (name === 'leg_adjustment') {
        if (this.animationsInProgress > 0) return
        this.handleLegrestAdjustment(direction)
        return
      }
      // calfrest
      if (name === 'calfrest_adjustment') {
        if (this.animationsInProgress > 0) return
        this.handleCalfrestAdjustment(direction)
        return
      }
      // siderails
      if (name === 'siderails') {
        if (this.animationsInProgress > 0) return
        this.handleSiderails()
        return
      }
      // handles
      if (name === 'handles') {
        if (this.animationsInProgress > 0) return
        this.handleHandles()
        return
      }
      // poles
      if (name === 'poles') {
        if (this.animationsInProgress > 0) return
        this.handlePoles()
        return
      }
      // drive
      if (name === 'drive') {
        if (this.animationsInProgress > 0) return
        this.handleDrive()
        return
      }
      // cpr
      if (name === 'cpr') {
        if (this.animationsInProgress > 0) return
        this.handleCpr(name)
        return
      }

			this.handleAnimationRequest(name, binaryAnimationActive || direction === 'down')
		})

		this.$bus.on('requestModelAnimationsReset', () => {
			this.resetAnimations(Object.keys(this.animationsList))
		})

		this.$bus.on('requestModelPartsVisibility', nodes => {
			nodes.forEach(node => {this.setModelNodeVisibility(node.name, node.visible)})
		})

		this.$bus.on('requestCameraPosition', cameraPosition => {
			this.setCameraPosition(cameraPosition)
		})

		window.modelVM = this // TODO: for debugging only, remove when going into production
	},
	methods: {
		onModelLoad(e) {
			const viewer = e.target
			const actions = {}

			// add all available animations to mixer and set reference for easy access
			for (const [name, animation] of Object.entries(this.animationsList)) {
				const animationTrack = viewer.scene.animationsByName.get(animation.track)

				if (animationTrack) {
					const action = viewer.animationMixer.clipAction(animationTrack)

					action.setLoop(viewer.availableLoopModes.once)
					action.clampWhenFinished = true

					actions[name] = action
				}
			}

			// set up hotspots (positioning is done by modelviewer)
			viewer.querySelectorAll('button').forEach(hotspot => {
				hotspot.setAttribute('disabled', '')
				hotspot.addEventListener('click', () => this.onHotspotClick(hotspot))
				this.hotspots.push(hotspot)
			})

			this.modelActions = actions
			this.modelViewer = viewer
			window.modelViewer = viewer
      window.modelActions = actions      
		},

    async handleCpr() {
      const trendelenburgTiming = this.modelActions['trendelenburg']?.time > 0
      const reverseTrendelenburgTiming = this.modelActions['reverse_trendelenburg']?.time > 0

      this.reverseBackrestAdjustment()
      this.reverseLegrestAdjustment()

      if (trendelenburgTiming) {
        this.modelActions['trendelenburg'].setEffectiveWeight(0)
        this.playAnimation('trendelenburg', true, 100)
        this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
        this.playAnimation('bed_height_adjustment_down', false, 100)

        this.modelActions['trendelenburg_to_min'].setEffectiveWeight(1)
        await this.playAnimation('trendelenburg_to_min', false)
      } else if (reverseTrendelenburgTiming) {
        this.modelActions['reverse_trendelenburg'].setEffectiveWeight(0)
        this.playAnimation('reverse_trendelenburg', true, 100)
        this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
        this.playAnimation('bed_height_adjustment_down', false, 100)

        this.modelActions['reversetrendelenburg_to_min'].setEffectiveWeight(1)
        await this.playAnimation('reversetrendelenburg_to_min', false)
      } else {
        this.modelActions['bed_height_adjustment_up'].setEffectiveWeight(0)
        this.playAnimation('bed_height_adjustment_up', true, 100)
        this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(1)
        await this.playAnimation('bed_height_adjustment_down', false)
      }
    },

    async handleHandles() {
      const handlesDownTiming = this.modelActions['pushhandles_down']?.time > 0

      if (handlesDownTiming) {
        this.modelActions['pushhandles_down'].setEffectiveWeight(0)
        this.playAnimation('pushhandles_down', true, 100)

        this.modelActions['pushhandles_up'].setEffectiveWeight(1)
        await this.playAnimation('pushhandles_up', false)
      } else {
        this.modelActions['pushhandles_up'].setEffectiveWeight(0)
        this.playAnimation('pushhandles_up', true, 100)

        this.modelActions['pushhandles_down'].setEffectiveWeight(1)
        await this.playAnimation('pushhandles_down', false)
      }
    },

    async handleDrive() {
      const idriveTiming = this.modelActions['idrive']?.time > 0

      if (idriveTiming) {
        this.modelActions['idrive'].setEffectiveWeight(0)
        this.playAnimation('idrive', true, 100)

        this.modelActions['idrive_rev'].setEffectiveWeight(1)
        await this.playAnimation('idrive_rev', false)
      } else {
        this.modelActions['idrive_rev'].setEffectiveWeight(0)
        this.playAnimation('idrive_rev', true, 100)

        this.modelActions['idrive'].setEffectiveWeight(1)
        await this.playAnimation('idrive', false)
      }
    },

    async handleSiderails() {
      const siderailsDownTiming = this.modelActions['folding_of_siderails_down']?.time > 0

      if (siderailsDownTiming) {
        this.modelActions['folding_of_siderails_down'].setEffectiveWeight(0)
        this.playAnimation('folding_of_siderails_down', true, 100)

        this.modelActions['folding_of_siderails_up'].setEffectiveWeight(1)
        await this.playAnimation('folding_of_siderails_up', false)
      } else {
        this.modelActions['folding_of_siderails_up'].setEffectiveWeight(0)
        this.playAnimation('folding_of_siderails_up', true, 100)

        this.modelActions['folding_of_siderails_down'].setEffectiveWeight(1)
        await this.playAnimation('folding_of_siderails_down', false)
      }
    },

    async handlePoles() {
      const polesRotationUpTiming = this.modelActions['poles_rotation_up']?.time > 0
      const polesRotationDownTiming = this.modelActions['poles_rotation_down']?.time > 0
      const polesMoveUpTiming = this.modelActions['poles_move_up']?.time > 0
      const polesMoveDownTiming = this.modelActions['poles_move_down']?.time > 0

      if (polesMoveDownTiming) {
        this.modelActions['poles_move_down'].setEffectiveWeight(0)
        this.playAnimation('poles_move_down', true, 100)
        this.modelActions['poles_rotation_up'].setEffectiveWeight(0)
        this.playAnimation('poles_rotation_up', true, 100)

        this.modelActions['poles_rotation_down'].setEffectiveWeight(1)
        await this.playAnimation('poles_rotation_down', false)
      } else if (polesMoveUpTiming) {
        this.modelActions['poles_move_up'].setEffectiveWeight(0)
        this.playAnimation('poles_move_up', true, 100)

        this.modelActions['poles_move_down'].setEffectiveWeight(1)
        await this.playAnimation('poles_move_down', false)
      } else if (polesRotationUpTiming) {
        this.modelActions['poles_move_up'].setEffectiveWeight(1)
        await this.playAnimation('poles_move_up', false)
      } else if (polesRotationDownTiming) {
        this.modelActions['poles_rotation_down'].setEffectiveWeight(0)
        this.playAnimation('poles_rotation_down', true, 100)
        
        this.modelActions['poles_rotation_up'].setEffectiveWeight(1)
        await this.playAnimation('poles_rotation_up', false)
      } else {
        this.modelActions['poles_rotation_up'].setEffectiveWeight(0)
        this.playAnimation('poles_rotation_up', true, 100)

        this.modelActions['poles_move_up'].setEffectiveWeight(1)
        await this.playAnimation('poles_move_up', false)
      }
    },

    async handleTrendelenburg(name) {
      const bhdTiming = this.modelActions['bed_height_adjustment_down']?.time > 0
      const trendelenburgTiming = this.modelActions['trendelenburg']?.time > 0
      const reverseTrendelenburgTiming = this.modelActions['reverse_trendelenburg']?.time > 0

      this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
      this.modelActions['bed_height_adjustment_up'].setEffectiveWeight(0)

      if (trendelenburgTiming) {
        this.modelActions['trendelenburg'].setEffectiveWeight(0)
        this.playAnimation('trendelenburg', true, 100)
        this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
        this.playAnimation('bed_height_adjustment_down', false, 100)

        this.modelActions['trendelenburg_to_min'].setEffectiveWeight(1)
        await this.playAnimation('trendelenburg_to_min', false)
      } else if (reverseTrendelenburgTiming) {
        this.modelActions['reverse_trendelenburg'].setEffectiveWeight(0)
        this.playAnimation('reverse_trendelenburg', true, 100)
        this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
        this.playAnimation('bed_height_adjustment_down', false, 100)

        this.modelActions['reversetrendelenburg_to_min'].setEffectiveWeight(1)
        await this.playAnimation('reversetrendelenburg_to_min', false)
      } else if (!bhdTiming) {

        if (name === 'trendelenburg') {
          this.modelActions['trendelenburg'].setEffectiveWeight(1)
          await this.playAnimation('trendelenburg', false)
        }
        if (name === 'reverse_trendelenburg') {
          this.modelActions['reverse_trendelenburg'].setEffectiveWeight(1)
          await this.playAnimation('reverse_trendelenburg', false)
        }

      }

      this.resetModel()
    },

    async handleBackrestAdjustment(direction) {

      const br30Timing = this.modelActions['backrest_adjustment30']?.time > 0
      const br60Timing = this.modelActions['backrest_adjustment60']?.time > 0
      const br90Timing = this.modelActions['backrest_adjustment90']?.time > 0

      if (direction === 'up') {
        if (br90Timing) {
          return
        } else if(br60Timing) {
          this.modelActions['backrest_adjustment90rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment90'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment60'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment90rev', true, 100)
          await this.playAnimation('backrest_adjustment90', false)
        } else if(br30Timing) {
          this.modelActions['backrest_adjustment60rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment60'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment30'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment60rev', true, 100)
          await this.playAnimation('backrest_adjustment60', false)
        } else {
          this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment30'].setEffectiveWeight(1)
          this.playAnimation('backrest_adjustment30rev', true, 100)
          await this.playAnimation('backrest_adjustment30', false)
        }
      } else {
        
        if (br90Timing) {
          this.modelActions['backrest_adjustment90'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment90rev'].setEffectiveWeight(1)
          this.playAnimation('backrest_adjustment90', true, 100)
          await this.playAnimation('backrest_adjustment90rev', false)
        } else if(br60Timing) {
          this.modelActions['backrest_adjustment90rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment60rev'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment60'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment90rev', true, 100)
          this.playAnimation('backrest_adjustment60', true, 100)
          await this.playAnimation('backrest_adjustment60rev', false)
        } else if(br30Timing) {
          this.modelActions['backrest_adjustment60rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment30'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment30', true, 100)
          this.playAnimation('backrest_adjustment60rev', true, 100)
          await this.playAnimation('backrest_adjustment30rev', false)
        } else {
          return
        }
      }
    },

    async handleLegrestAdjustment(direction) {
      const leg01Timing = this.modelActions['leg_rest_adjustment01']?.time > 0
      const leg02Timing = this.modelActions['leg_rest_adjustment02']?.time > 0
      const calfrestTiming = this.modelActions['calf_rest_adjustment']?.time > 0
      const calfrest01Timing = this.modelActions['calf_rest_adjustment01']?.time > 0

      if (calfrestTiming || calfrest01Timing) return

      if (direction === 'up') {
        if (leg02Timing) {
          return
        } else if(leg01Timing) {
          this.modelActions['calf_rest_adjustment01_rev'].setEffectiveWeight(0)
          this.playAnimation('calf_rest_adjustment01_rev', true, 100)
          this.modelActions['leg_rest_adjustment02rev'].setEffectiveWeight(0)
          this.playAnimation('leg_rest_adjustment02rev', true, 100)
          this.modelActions['leg_rest_adjustment01'].setEffectiveWeight(0)
          this.modelActions['leg_rest_adjustment02'].setEffectiveWeight(1)
          await this.playAnimation('leg_rest_adjustment02', false)
        } else {
          this.modelActions['leg_rest_adjustment01rev'].setEffectiveWeight(0)
          this.playAnimation('leg_rest_adjustment01rev', true, 100)

          this.modelActions['leg_rest_adjustment01'].setEffectiveWeight(1)
          await this.playAnimation('leg_rest_adjustment01', false)
        }
      } else {
        if (leg02Timing) {
          this.modelActions['calf_rest_adjustment_rev'].setEffectiveWeight(0)
          this.playAnimation('calf_rest_adjustment_rev', true, 100)
          this.modelActions['leg_rest_adjustment02'].setEffectiveWeight(0)
          this.playAnimation('leg_rest_adjustment02', true, 100)

          this.modelActions['leg_rest_adjustment02rev'].setEffectiveWeight(1)
          await this.playAnimation('leg_rest_adjustment02rev', false)
        } else if(leg01Timing) {
          this.modelActions['calf_rest_adjustment01_rev'].setEffectiveWeight(0)
          this.playAnimation('calf_rest_adjustment01_rev', true, 100)
          this.modelActions['leg_rest_adjustment02rev'].setEffectiveWeight(0)
          this.playAnimation('leg_rest_adjustment02rev', true, 100)
          this.modelActions['leg_rest_adjustment01'].setEffectiveWeight(0)
          this.playAnimation('leg_rest_adjustment01', true, 100)

          this.modelActions['leg_rest_adjustment01rev'].setEffectiveWeight(1)
          await this.playAnimation('leg_rest_adjustment01rev', false)
        } else {
          return
        }
      }
    },

    async handleCalfrestAdjustment(direction) {
      const leg01Timing = this.modelActions['leg_rest_adjustment01']?.time > 0
      const leg02Timing = this.modelActions['leg_rest_adjustment02']?.time > 0
      const calfrestTiming = this.modelActions['calf_rest_adjustment']?.time > 0
      const calfrest01Timing = this.modelActions['calf_rest_adjustment01']?.time > 0

      if (leg02Timing) {
        if (direction === 'up') {
          this.modelActions['calf_rest_adjustment_rev'].setEffectiveWeight(0)
          this.playAnimation('calf_rest_adjustment_rev', true, 100)
          this.modelActions['leg_rest_adjustment02'].setEffectiveWeight(0)

          this.modelActions['calf_rest_adjustment'].setEffectiveWeight(1)
          await this.playAnimation('calf_rest_adjustment', false)
        } else if (calfrestTiming) {
          this.modelActions['calf_rest_adjustment'].setEffectiveWeight(0)
          this.playAnimation('calf_rest_adjustment', true, 100)

          this.modelActions['calf_rest_adjustment_rev'].setEffectiveWeight(1)
          await this.playAnimation('calf_rest_adjustment_rev', false)
        }
      } else if (leg01Timing) {
        if (direction === 'up') {
        this.modelActions['leg_rest_adjustment02rev'].setEffectiveWeight(0)
          this.playAnimation('leg_rest_adjustment02rev', true, 100)
          this.modelActions['calf_rest_adjustment01_rev'].setEffectiveWeight(0)
          this.playAnimation('calf_rest_adjustment01_rev', true, 100)
          this.modelActions['leg_rest_adjustment01'].setEffectiveWeight(0)

          this.modelActions['calf_rest_adjustment01'].setEffectiveWeight(1)
          await this.playAnimation('calf_rest_adjustment01', false)
        } else if (calfrest01Timing) {
          this.modelActions['calf_rest_adjustment01'].setEffectiveWeight(0)
          this.playAnimation('calf_rest_adjustment01', true, 100)

          this.modelActions['calf_rest_adjustment01_rev'].setEffectiveWeight(1)
          await this.playAnimation('calf_rest_adjustment01_rev', false)
        }
      }
    },

    async handleBedHeightAdjustment(name) {

      const bhdTiming = this.modelActions['bed_height_adjustment_down']?.time > 0
      const trendelenburgTiming = this.modelActions['trendelenburg']?.time > 0
      const reverseTrendelenburgTiming = this.modelActions['reverse_trendelenburg']?.time > 0

      if (name === 'bed_height_adjustment_up') {
        if (bhdTiming) {
          this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
          this.playAnimation('bed_height_adjustment_down', true, 100)

          this.modelActions['bed_height_adjustment_up'].setEffectiveWeight(1)
          await this.playAnimation('bed_height_adjustment_up', false)
        } else if (trendelenburgTiming) {
          this.modelActions['trendelenburg'].setEffectiveWeight(0)
          this.playAnimation('trendelenburg', true, 100)

          this.modelActions['trendelenburg_to_max'].setEffectiveWeight(1)
          await this.playAnimation('trendelenburg_to_max', false)
        } else if (reverseTrendelenburgTiming) {
          this.modelActions['reverse_trendelenburg'].setEffectiveWeight(0)
          this.playAnimation('reverse_trendelenburg', true, 100)

          this.modelActions['reversetrendelenburg_to_max'].setEffectiveWeight(1)
          await this.playAnimation('reversetrendelenburg_to_max', false)
        }
      }
      if (name === 'bed_height_adjustment_down') {
        if (trendelenburgTiming) {
          this.modelActions['trendelenburg'].setEffectiveWeight(0)
          this.playAnimation('trendelenburg', true, 100)
          this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
          this.playAnimation('bed_height_adjustment_down', false, 100)

          this.modelActions['trendelenburg_to_min'].setEffectiveWeight(1)
          await this.playAnimation('trendelenburg_to_min', false)
        } else if (reverseTrendelenburgTiming) {
          this.modelActions['reverse_trendelenburg'].setEffectiveWeight(0)
          this.playAnimation('reverse_trendelenburg', true, 100)
          this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
          this.playAnimation('bed_height_adjustment_down', false, 100)

          this.modelActions['reversetrendelenburg_to_min'].setEffectiveWeight(1)
          await this.playAnimation('reversetrendelenburg_to_min', false)
        } else {
          this.modelActions['bed_height_adjustment_up'].setEffectiveWeight(0)
          this.playAnimation('bed_height_adjustment_up', true, 100)

          this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(1)
          await this.playAnimation('bed_height_adjustment_down', false)
        }
      }

      this.resetModel()
    },

    async resetModel() {
      
      this.modelActions['trendelenburg_to_max'].setEffectiveWeight(0)
      this.modelActions['reversetrendelenburg_to_max'].setEffectiveWeight(0)
      this.modelActions['trendelenburg_to_min'].setEffectiveWeight(0)
      this.modelActions['reversetrendelenburg_to_min'].setEffectiveWeight(0)
      this.modelActions['bed_height_adjustment_up'].setEffectiveWeight(0)

      this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(1)

      await this.playAnimation('trendelenburg_to_max', true, 100)
      await this.playAnimation('bed_height_adjustment_up', true, 100)
      await this.playAnimation('reversetrendelenburg_to_max', true, 100)
      await this.playAnimation('trendelenburg_to_min', true, 100)
      await this.playAnimation('reversetrendelenburg_to_min', true, 100)
    },

		async handleAnimationRequest(animationName, reverse) {
			this.hideInteractionPrompt()
      
      if (this.$store.state.activeProduct === 'multicare') {
        await this.resetModel()
      }

			let animationConfig = this.animationsList[animationName]
      let isOppositeAnimationActive = this.modelActions[animationConfig.opposite]?.time > 0

      if (animationConfig.conditional?.length) {

        for (const anim of animationConfig.conditional) {
          const isConditionalAnimationActive = this.modelActions[anim.conditionalAnimation]?.time > 0

          if (isConditionalAnimationActive) {
            animationName = anim.substituteAnimation
            animationConfig = this.animationsList[animationName]
            isOppositeAnimationActive = false
          }
        }

        if (animationConfig.overrides?.length) {
          this.modelActions[animationName].setEffectiveWeight(1)

          for (const anim of animationConfig.overrides) {
            this.modelActions[anim.animation].setEffectiveWeight(0)
            if (this.$store.state.activeProduct === 'multicare') {
              this.playAnimation(anim.animation, anim.reverse, 100)
            } else {
              await this.playAnimation(anim.animation, anim.reverse, 100)
            }
          }
        }
      }

			if (isOppositeAnimationActive) {
				console.log('Reversing opposite animation:', animationConfig.opposite || null)
				await this.resetAnimations([animationConfig.opposite])
			} else {

        // reverse in order
        if ('reverseInOrder' in animationConfig) {
          for (const anim of animationConfig.reverseInOrder) {
            if (anim === 'backrest_adjustment') {
              this.reverseBackrestAdjustment()
            } else if (anim === 'leg_adjustment') {
              if (animationName === 'vascular_position' || animationName === 'mobilization_position' || animationName === 'mobilization_from_min') {
                await this.reverseLegAdjustment()
              } else {
                this.reverseLegAdjustment()
              }
            } else {
              await this.playAnimation(anim, true, 5)
            }
          }
        }

				await this.resetAnimations(animationConfig.conflicting, 5)
				console.log('Conflicting animations reversed')

        if (reverse && 'reversable' in animationConfig) {
          if (animationConfig.reversable) {
            this.playAnimation(animationName, reverse)
          }
        } else {
          this.playAnimation(animationName, reverse)
        }
			}
		},

		playAnimation(name, reverse, speed = 1) {
			return new Promise(resolve => {
				console.log(`${reverse ? 'Reversing' : 'Playing'} animation:`, name)
				const action = this.modelActions[name]
				const timeScaleSign = reverse ? -1 : 1

				// start the rendering loop
				this.modelViewer.play()

				action.setEffectiveTimeScale(speed * timeScaleSign)
				action.enabled = true
				action.paused = false
				action.play()

				this.animationsInProgress = this.animationsInProgress + 1

				// set up listener when animation finishes
				// assigning the callback to action.finishedCallback makes the listener unique so that it can be removed afterwards
				this.modelViewer.animationMixer.addEventListener('finished', action.finishedCallback = e => {
					const finishedTrackName = e.action.getClip().name

					if (finishedTrackName === this.animationsList[name].track) {
						this.modelViewer.animationMixer.removeEventListener('finished', action.finishedCallback)

						// save resources by pausing the rendering loop when no animations are playing
						if (this.animationsInProgress === 1) {
							this.modelViewer.pause()
						}
						this.animationsInProgress = this.animationsInProgress - 1

						action.enabled = !reverse

						resolve()
					}
				})
			})
		},

    async reverseLegrestAdjustment() {     

      const leg01Timing = this.modelActions['leg_rest_adjustment01']?.time > 0
      const leg02Timing = this.modelActions['leg_rest_adjustment02']?.time > 0
      const calfrestTiming = this.modelActions['calf_rest_adjustment']?.time > 0

      if (calfrestTiming) {
        this.modelActions['calf_rest_adjustment'].setEffectiveWeight(0)
        this.playAnimation('calf_rest_adjustment', true, 100)

        this.modelActions['calf_rest_adjustment_rev'].setEffectiveWeight(1)
        await this.playAnimation('calf_rest_adjustment_rev', false)
      }
      if (leg02Timing) {
        this.modelActions['calf_rest_adjustment_rev'].setEffectiveWeight(0)
        this.playAnimation('calf_rest_adjustment_rev', true, 100)
        this.modelActions['leg_rest_adjustment02'].setEffectiveWeight(0)
        this.playAnimation('leg_rest_adjustment02', true, 100)

        this.modelActions['leg_rest_adjustment02rev'].setEffectiveWeight(1)
        await this.playAnimation('leg_rest_adjustment02rev', false)
      }
      if(leg01Timing) {
        this.modelActions['leg_rest_adjustment02rev'].setEffectiveWeight(0)
        this.playAnimation('leg_rest_adjustment02rev', true, 100)
        this.modelActions['leg_rest_adjustment01'].setEffectiveWeight(0)
        this.playAnimation('leg_rest_adjustment01', true, 100)

        this.modelActions['leg_rest_adjustment01rev'].setEffectiveWeight(1)
        await this.playAnimation('leg_rest_adjustment01rev', false)
      }
    },

    async reverseBackrestAdjustment() {
      const br30Timing = this.modelActions['backrest_adjustment30']?.time > 0
      const br60Timing = this.modelActions['backrest_adjustment60']?.time > 0
      const br90Timing = this.modelActions['backrest_adjustment90']?.time > 0

      if (br90Timing) {
        this.modelActions['backrest_adjustment90'].setEffectiveWeight(0)
        this.modelActions['backrest_adjustment90rev'].setEffectiveWeight(1)
        this.playAnimation('backrest_adjustment90', true, 100)
        await this.playAnimation('backrest_adjustment90rev', false)
      }
      
      if(br60Timing) {
        this.modelActions['backrest_adjustment90rev'].setEffectiveWeight(0)
        this.modelActions['backrest_adjustment60rev'].setEffectiveWeight(1)
        this.modelActions['backrest_adjustment60'].setEffectiveWeight(0)
        this.playAnimation('backrest_adjustment90rev', true, 100)
        this.playAnimation('backrest_adjustment60', true, 100)
        await this.playAnimation('backrest_adjustment60rev', false)
      }
      
      if(br30Timing) {
        this.modelActions['backrest_adjustment60rev'].setEffectiveWeight(0)
        this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(1)
        this.modelActions['backrest_adjustment30'].setEffectiveWeight(0)
        this.playAnimation('backrest_adjustment30', true, 100)
        this.playAnimation('backrest_adjustment60rev', true, 100)
        await this.playAnimation('backrest_adjustment30rev', false)
        this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(0)
      }
    },

		resetAnimations(animationNames = [], speed = 1) {
			const animationsPromises = []

			animationNames.forEach(animationName => {
				if (this.modelActions[animationName]?.time > 0) {
					animationsPromises.push(this.playAnimation(animationName, true, speed))
				}
			})

			return Promise.all(animationsPromises)
		},

		setModelNodeVisibility(nodeName, visible) {
      if (nodeName === 'idrive_geoShape') {
        this.productConfig.secondaryMenu.controls.find(item => item.animation === 'drive').visible = visible
      }
			const node = this.modelViewer.model.primitives.find(it => it.name === nodeName)
			node.setMeshVisibility(visible)
		},

		updateProgress(e) {
			this.$store.commit('setModelLoadingProgress', e.detail.totalProgress)
		},

		hideInteractionPrompt() {
			this.modelPrompt = 'none'
		},

		setCameraPosition(position) {
			this.modelViewer.cameraOrbit = position.orbit
			this.modelViewer.cameraTarget = position.target
		},

		resetCameraPosition() {
			this.setCameraPosition({
				orbit: '-50deg 75deg 65%',
				target: 'auto auto auto'
			})
		},

		onHotspotClick(hotspot) {
			const explanations = hotspot.dataset.explanations?.split('.')
			if (explanations) {
				const explanation = explanations[0]
				const subExplanation = explanations[1] || null
        const explanationItem = this.productConfig.explanations[explanation]
        const subExplanationItem = this.productConfig.explanations[subExplanation]
				this.$store.commit('navigation/setActiveSecondary', 'key_features.' + explanation)
				this.$store.commit('setExplanationPaneContent', explanationItem)
				this.$store.commit('setExplanationPaneSub', subExplanation)
				this.$store.commit('setExplanationPaneOpen', true)

        if (subExplanationItem?.camera) {
          this.setCameraPosition(subExplanationItem.camera)
        }
			}
		},

		onCameraChange() {
			this.cameraOrbit = this.modelViewer?.getCameraOrbit() || ''
			this.cameraTarget = this.modelViewer?.getCameraTarget() || ''
		}
	},
  watch: {
		activeSecondaryNavigation(val) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// disable hotspots not related to active feature
				this.hotspots.forEach(hotspot => {
					const navItemName = val.split('.')[1]
					const hotspotExplanations = hotspot.dataset.explanations?.split('.')
					hotspot.classList.remove('active')
					if (hotspotExplanations[0] === navItemName) {
						if (hotspotExplanations.length === 1) {
							hotspot.classList.add('active')
						}
						hotspot.removeAttribute('disabled')
					} else {
						hotspot.setAttribute('disabled', '')
					}
				})
			}
		},
		activeSubExplanation(name) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// display hotspot as active when related feature + subExplanation is selected
				this.hotspots.forEach(hotspot => {
					const hotspotExplanation = hotspot.dataset.explanations?.split('.')[1]
					if (hotspotExplanation === name) {
						hotspot.classList.add('active')
					} else {
						hotspot.classList.remove('active')
					}
				})
			}
		},
		explanationPaneOpen(open) {
			if (!open) {
				this.$nextTick(() => {
					this.resetCameraPosition()
				})
			}
		}
	},
}
</script>

<style lang="scss" scoped>
model-viewer {
	width: 100%;
	height: 100%;
	--poster-color: transparent;
	--min-hotspot-opacity: 0;

	&::part(default-progress-bar) {
		top: 30px;
		background-color: $clrRed;
	}
}

::v-deep(.Hotspot) {
	display: none;
	position: relative;
	width: 26px;
	height: 26px;
	background-color: transparentize($color: $clrWhite, $amount: 0.2);
	border-radius: 100px;
	box-shadow: 0 0 0 4px transparentize($color: $clrWhite, $amount: 0.5);
	transition: all 300ms ease 200ms;
	&.active,
	&:hover,
	&:disabled,
	&:not([data-visible]) {
		background-color: transparent;
		box-shadow: 0 0 0 0 transparent;
		transition: all 300ms ease;
	}
	&:disabled {
		pointer-events: none;
	}
	.hotspots-visible & {
		display: block;
	}

	&::before {
		content: "";
		position: absolute;
		width: 17px;
		height: 17px;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		border-radius: 100px;
		border: 1px solid $clrWhite;
		background-color: $clrRed;
		background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M128 29.799v196.403M226.202 128H29.799' stroke-linecap='round' stroke-miterlimit='10' stroke-width='51.7px' fill='none' stroke='%23FFFFFF'/%3E%3C/svg%3E%0A");
		background-size: 10px;
		background-repeat: no-repeat;
		background-position: center;
		transition: all 300ms ease 200ms;
	}
	&.active::before,
	&:hover::before,
	&:disabled::before,
	&:not([data-visible])::before {
		transform: translate(-50%, -50%) scale(0.6);
		opacity: 0;
		transition: all 300ms ease;
	}

	.HotspotAnnotation {
		position: absolute;
		left: 50%;
		bottom: 24px;
		padding: 0.2em 0.75em;
		transform: translate(-50%, 30%);
		opacity: 0;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
		border-radius: 100px;
		background-color: $clrWhite;
		transition: all 300ms ease;
		pointer-events: none;
		z-index: 100;

		&::before {
			content: "";
			position: absolute;
			width: 0;
			height: 0;
			bottom: -6px;
			left: calc(50% - 6px);
			border-style: solid;
			border-width: 6px 6px 0 6px;
			border-color: #ffffff transparent transparent transparent;
		}
	}
	&:hover, &.active {
		.HotspotAnnotation {
			opacity: 1;
			transform: translate(-50%, 0);
			pointer-events: all;
			color: $clrRed;
		}
	}
}

.debug-info {
	position: absolute;
	top: 50px;
	left: 300px;
	font-size: 10px;
}
</style>
